import React from 'react'
import { Hook } from '../hooks/Hook'
import { useMVUReducer } from '../mvuReducer'
import { applyAction, initialize, makeSideEffectExecutor, Toast, Dispatch } from './toasts'
import { usePubSubSubscribe } from '~/pubSub/usePubSubSubscribe'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { IconButton } from '~/components/IconButton'

export function Toasts({ hook }: { hook: Hook }) {
  const toasts = hook.el.dataset.flash ? (JSON.parse(hook.el.dataset.flash) as Toast[]) : []

  const [state, dispatch] = useMVUReducer(initialize(toasts), applyAction, makeSideEffectExecutor(hook))

  usePubSubSubscribe<Toast>(
    'show-toast',
    (toast) => {
      dispatch({ action: 'add-toast', toast })
    },
    [dispatch]
  )

  return (
    <div className="flex flex-col gap-4">
      {state.toasts.map((toast) => {
        return <Toast key={toast.content} toast={toast} dispatch={dispatch} />
      })}
    </div>
  )
}

function Toast({ toast, dispatch }: { toast: Toast; dispatch: Dispatch }) {
  return (
    <div
      data-qa="toast"
      className={containerClass(toast.type)}
      onClick={() => dispatch({ action: 'dismiss-toast', toast: toast })}
    >
      <div className="py-2 flex flex-row items-center gap-2">
        <ToastIcon type={toast.type} />
        {toast.content}
      </div>
      <IconButton
        icon={<XMarkIcon className="w-6 h-6 icon-primary" />}
        screenReaderText="Dismiss"
        onClick={() => dispatch({ action: 'dismiss-toast', toast: toast })}
      />
    </div>
  )
}

function ToastIcon({ type }: { type: Toast['type'] }) {
  switch (type) {
    case 'error':
      return <ExclamationCircleIcon className="w-6 h-6 shrink-0 icon-danger" />
    case 'warning':
      return <ExclamationTriangleIcon className="w-6 h-6 shrink-0 icon-warning" />
    case 'success':
      return <CheckCircleIcon className="w-6 h-6 shrink-0 icon-success" />
    case 'info':
      return <InformationCircleIcon className="w-6 h-6 shrink-0 icon-info" />
  }
}

function containerClass(type: Toast['type']): string {
  const commonClasses = `
    flex flex-row items-center justify-between gap-2 sm:w-full group cursor-pointer
    pl-3 pr-1 py-1 rounded-lg shadow-xl border bg-primary
    text-primary text-sm font-medium
  `

  switch (type) {
    case 'error':
      return `${commonClasses} border-danger`
    case 'warning':
      return `${commonClasses} border-warning-inverse`
    case 'success':
      return `${commonClasses} border-success-inverse`
    case 'info':
      return `${commonClasses} border-info`
  }
}
